import { supabase } from '../lib/supabase';

// ─── RULES CRUD ────────────────────────────────────────────────────────────────

export const getRules = async (activeOnly = false) => {
  let query = supabase?.from('avatar_asset_rules')?.select('*')?.order('created_at', { ascending: false });
  if (activeOnly) query = query?.eq('is_active', true);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const upsertRule = async (rule) => {
  const { id, ...rest } = rule;
  if (id) {
    const { data, error } = await supabase?.from('avatar_asset_rules')?.update(rest)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase?.from('avatar_asset_rules')?.insert(rest)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

export const deleteRule = async (id) => {
  const { error } = await supabase?.from('avatar_asset_rules')?.delete()?.eq('id', id);
  if (error) throw error;
};

// ─── COMPATIBILITY EVALUATION ─────────────────────────────────────────────────

/**
 * Evaluate which categories/slugs are disabled given the current avatar config.
 * Returns: { disabledCategories: Set<string>, disabledSlugs: Set<string> }
 *
 * avatarConfig shape: { bodyType, skinTone, eyes, eyeColor, eyebrows, mouth,
 *   hairStyle, hairColor, facialHair, clothing, costume, hat, accessory,
 *   wings, effect, specialHead }
 *
 * selectedSlugs: array of currently selected asset slugs
 */
export const evaluateCompatibility = (rules, selectedSlugs = [], avatarConfig = {}) => {
  const disabledCategories = new Set();
  const disabledSlugs = new Set();

  // Map from avatarConfig keys to category slugs for trigger_category checks
  const configKeyToCategory = {
    background: 'backgrounds',
    bodyType: 'bodies',
    eyes: 'eyes',
    eyebrows: 'eyebrows',
    mouth: 'mouths',
    hairStyle: 'hair_styles',
    facialHair: 'facial_hair',
    clothing: 'clothing',
    accessory: 'accessories',
    hat: 'accessories',
    wings: 'fantasy',
    costume: 'clothing',
    specialHead: 'fantasy',
    effect: 'fantasy',
  };

  // Build a set of category slugs that currently have a selection
  const selectedCategories = new Set();
  for (const [key, categorySlug] of Object.entries(configKeyToCategory)) {
    if (avatarConfig?.[key]) selectedCategories?.add(categorySlug);
  }

  const activeRules = rules?.filter(r => r?.is_active);

  for (const rule of activeRules) {
    // Check if trigger is active
    let triggered = false;

    if (rule?.trigger_slug) {
      triggered = selectedSlugs?.includes(rule?.trigger_slug);
    } else if (rule?.trigger_category) {
      // Only trigger if the trigger category actually has a selection
      triggered = selectedCategories?.has(rule?.trigger_category);
    }

    if (!triggered) continue;

    if (rule?.rule_type === 'disables' || rule?.rule_type === 'conflicts_with') {
      if (rule?.affected_category) disabledCategories?.add(rule?.affected_category);
      if (rule?.affected_slug) disabledSlugs?.add(rule?.affected_slug);
    }
  }

  return { disabledCategories, disabledSlugs };
};

/**
 * Check if a specific asset slug is compatible with the current selection.
 */
export const isAssetCompatible = (assetSlug, assetCategory, rules, selectedSlugs, avatarConfig) => {
  const { disabledCategories, disabledSlugs } = evaluateCompatibility(rules, selectedSlugs, avatarConfig);
  if (disabledSlugs?.has(assetSlug)) return false;
  if (disabledCategories?.has(assetCategory)) return false;
  return true;
};
